import { gql } from '@apollo/client';
import { BoxFooter, Button, Flag, Input, Spinner, Text, isFlagCode } from '@mnd-frontend/ui';
import { useState } from 'react';
import styled from 'styled-components';
import { useQuickNavigationQuery } from '../../../../../generated/hooks';
import useDebounce from '../../../../../hooks/useDebounce';
import mndUrl, { SubDomain } from '../../../../../lib/mndUrl';
import routes from '../../../../../routes';

gql`
  query QuickNavigation($query: String!) {
    sourceList(query: $query) {
      id
      name
      pressrooms {
        id
        siteCode
      }
    }
  }
`;

const MarginBottom = styled.div`
  margin-bottom: 1rem;
`;

const Ul = styled.ul`
  list-style-type: none;
  padding-left: 0;
`;

const StyledA = styled.a`
  background: transparent;
  border: none;
  float: right;
  margin-right: 5px;
  padding: 0;
`;

type QuickNavigateProps = {
  onClose?: () => void;
  redirectTo?: string;
};

const QuickNavigate: React.FC<QuickNavigateProps> = ({ onClose, redirectTo = '' }) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);

  const tooShortQuery = query.length < 3;

  const { data, loading } = useQuickNavigationQuery({
    variables: {
      query: debouncedQuery,
    },
    skip: tooShortQuery,
  });

  const sources = data?.sourceList ?? [];

  return (
    <>
      <div>
        <p>Search by Source Id, Pressroom Id, Source Name, Pressroom Name or Org Number.</p>
        <MarginBottom>
          <Input
            autoFocus
            onChange={e => setQuery(e.target.value)}
            label="Search"
            value={query}
            trailingIcon={loading ? <Spinner size="sm" /> : null}
          />
        </MarginBottom>
        {sources.length > 0 && (
          <Ul>
            {sources.map(source => (
              <li key={source.id}>
                <a href={mndUrl(SubDomain.WWW, `/admin/source/edit/${source.id}`)}>{source.name}</a>{' '}
                {source.pressrooms.map(pressroom => (
                  <StyledA
                    key={pressroom.id}
                    href={mndUrl(
                      SubDomain.Publish,
                      redirectTo
                        ? `/${pressroom.id}${redirectTo}`
                        : routes.dashboard({ pressroomId: pressroom.id }),
                    )}
                  >
                    <Flag
                      code={isFlagCode(pressroom.siteCode) ? pressroom.siteCode : undefined}
                      size="1rem"
                    />
                  </StyledA>
                ))}
              </li>
            ))}
          </Ul>
        )}
        {sources.length === 0 && !tooShortQuery && !loading && (
          <Text as="p" variant="body">
            No hits.
          </Text>
        )}
        {sources.length === 0 && tooShortQuery && !loading && (
          <Text as="p" variant="body">
            Input atleast three characters to trigger search.
          </Text>
        )}
      </div>

      {onClose && (
        <BoxFooter>
          <Button type="button" configuration="text" onClick={onClose}>
            Close
          </Button>
        </BoxFooter>
      )}
    </>
  );
};

export default QuickNavigate;
